import {
  AVAILABILITY_RESULTS,
  BOOKING_FLOW_ANALYTICS_EVENT_TYPES,
  BOOKING_FLOW_BACKEND_EVENT_TYPES,
  BOOKING_FLOW_CLIENT_EVENT_TYPES,
  BOOKING_FLOW_RESERVATION_MODES,
  RESERVATION_REQUEST_REJECTION_KINDS,
  TIME_SELECTION_SOURCES,
  TRANSPORT_FAILURE_KINDS,
  VALIDATION_BLOCK_REASONS,
  type BookingFlowAnalyticsEvent,
  type BookingFlowBackendEvent,
  type BookingFlowClientEventInput,
  type BookingFlowClientEventType,
  type BookingFlowBackendEventType,
  type PersistedBookingFlowClientEvent,
} from './bookingFlowAnalyticsTypes.ts';

export interface BookingFlowAnalyticsValidationIssue {
  code: string;
  path: string;
  message: string;
}

export type BookingFlowAnalyticsValidationResult<T> =
  | { valid: true; value: T }
  | { valid: false; issues: BookingFlowAnalyticsValidationIssue[] };

type Shape = 'clientInput' | 'persistedClient' | 'backend' | 'any';

const MAX_ID_LENGTH = 128;
const MAX_KEY_LENGTH = 256;
const MAX_TIME_BUCKET_LENGTH = 16;
const MAX_EVENT_BYTES = 8 * 1024;
const MAX_NESTING_DEPTH = 32;
const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/;
const IDEMPOTENCY_KEY = /^[A-Za-z0-9][A-Za-z0-9:._/-]{0,255}$/;
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const TIME_BUCKET = /^\d{2}:\d{2}$/;
const IANA_TIMEZONE_SHAPE = /^(?:UTC|[A-Za-z_]+(?:\/[A-Za-z0-9_+.-]+)+)$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
const PROHIBITED_KEYS = new Set([
  'name',
  'email',
  'phone',
  'telephone',
  'note',
  'message',
  'customFields',
  'token',
  'manageToken',
  'adminActionToken',
  'stack',
  'stackTrace',
  'rawError',
  'requestPayload',
  'ip',
  'ipAddress',
]);

const CLIENT_EVENT_TYPES = new Set<string>(BOOKING_FLOW_CLIENT_EVENT_TYPES);
const BACKEND_EVENT_TYPES = new Set<string>(BOOKING_FLOW_BACKEND_EVENT_TYPES);
const ALL_EVENT_TYPES = new Set<string>(BOOKING_FLOW_ANALYTICS_EVENT_TYPES);
const CLIENT_SERVER_OWNED_FIELDS = new Set(['receivedAtServer', 'normalization', 'backendIdempotencyKey', 'reservationId', 'occurredAtServer']);
const CORRELATION_FIELDS = new Set(['selectionRevision', 'availabilityRevision', 'submitAttemptId']);
const EVENT_SPECIFIC_FIELDS = new Set([
  'requestedDateKey',
  'headcount',
  'startTimeBucket',
  'source',
  'availabilityResult',
  'availabilityDecisionId',
  'reasonCode',
  'transportFailureKind',
  'rejectionKind',
  'reservationMode',
  'initialStatus',
]);

const CLIENT_COMMON_KEYS = ['schemaVersion', 'authority', 'eventId', 'eventType', 'unitId', 'visitorId', 'sessionId', 'bookingFlowId', 'occurredAtClient', 'clientTimezone', 'idempotencyKey', 'payload'] as const;
const PERSISTED_CLIENT_EXTRA_KEYS = ['receivedAtServer', 'normalization'] as const;
const BACKEND_COMMON_KEYS = ['schemaVersion', 'authority', 'eventId', 'eventType', 'unitId', 'visitorId', 'sessionId', 'bookingFlowId', 'submitAttemptId', 'occurredAtServer', 'backendIdempotencyKey', 'payload'] as const;

const selectionEventTypes = new Set<BookingFlowClientEventType>([
  'date_selected',
  'headcount_selected',
  'time_selected',
  'availability_result_presented',
  'contact_form_started',
  'reservation_validation_blocked',
  'reservation_submit_attempted',
]);
const availabilityEventTypes = new Set<BookingFlowClientEventType>(['availability_result_presented']);
const clientSubmitEventTypes = new Set<BookingFlowClientEventType>([
  'reservation_submit_attempted',
  'reservation_submit_transport_failed',
]);
const backendSubmitEventTypes = new Set<BookingFlowBackendEventType>([
  'reservation_request_rejected',
  'reservation_request_created',
]);

type IssueSink = BookingFlowAnalyticsValidationIssue[];

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const hasOwn = (value: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(value, key);
const issue = (issues: IssueSink, code: string, path: string, message: string) => issues.push({ code, path, message });
const unique = <T extends readonly string[]>(values: T) => new Set<string>(values);
const serializedBytes = (value: unknown, issues: IssueSink) => {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    issue(issues, 'non_serializable_value', '', 'event contains non-serializable data');
    return null;
  }
};

const isIsoTimestamp = (value: unknown) => typeof value === 'string' && ISO_TIMESTAMP.test(value) && !Number.isNaN(Date.parse(value));
const isDateKey = (value: unknown) => {
  if (typeof value !== 'string' || !DATE_KEY.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(time)) return false;
  return new Date(time).toISOString().slice(0, 10) === value;
};
const isPositiveInteger = (value: unknown) => Number.isSafeInteger(value) && Number(value) > 0;
const isIdentifier = (value: unknown) => typeof value === 'string' && value.length <= MAX_ID_LENGTH && IDENTIFIER.test(value);
const isIdempotencyKey = (value: unknown) => typeof value === 'string' && value.length <= MAX_KEY_LENGTH && IDEMPOTENCY_KEY.test(value);
const isTimeBucket = (value: unknown) => {
  if (typeof value !== 'string' || value.length > MAX_TIME_BUCKET_LENGTH || !TIME_BUCKET.test(value)) return false;
  const [hour, minute] = value.split(':').map(Number);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
};
const enumContains = (values: readonly string[], value: unknown) => typeof value === 'string' && values.includes(value);

const scanInputSafety = (value: unknown, issues: IssueSink, path = '', seen = new WeakSet<object>(), depth = 0) => {
  if (depth > MAX_NESTING_DEPTH) {
    issue(issues, 'max_depth_exceeded', path, 'event nesting exceeds maximum depth');
    return;
  }
  if (typeof value === 'bigint') {
    issue(issues, 'non_serializable_value', path, 'BigInt values are not serializable analytics data');
    return;
  }
  if (!value || typeof value !== 'object') return;
  if (seen.has(value)) {
    issue(issues, 'circular_reference', path, 'Circular references are not serializable analytics data');
    return;
  }
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((child, index) => scanInputSafety(child, issues, `${path}[${index}]`, seen, depth + 1));
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const childPath = path ? `${path}.${key}` : key;
    if (PROHIBITED_KEYS.has(key)) issue(issues, 'prohibited_key', childPath, `Prohibited analytics key: ${key}`);
    scanInputSafety(child, issues, childPath, seen, depth + 1);
  }
};

const validateAllowedKeys = (value: Record<string, unknown>, allowed: Set<string>, issues: IssueSink, path: string) => {
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) issue(issues, 'unknown_field', path ? `${path}.${key}` : key, 'Unknown field');
  }
};

const requireStringId = (record: Record<string, unknown>, key: string, issues: IssueSink) => {
  if (!isIdentifier(record[key])) issue(issues, 'invalid_identifier', key, `${key} must be a non-empty bounded opaque identifier`);
};

const requireIdempotencyKey = (record: Record<string, unknown>, key: string, issues: IssueSink) => {
  if (!isIdempotencyKey(record[key])) issue(issues, 'invalid_idempotency_key', key, `${key} must be a non-empty bounded idempotency key`);
};

const requireTimestamp = (record: Record<string, unknown>, key: string, issues: IssueSink) => {
  if (!isIsoTimestamp(record[key])) issue(issues, 'invalid_timestamp', key, `${key} must be an ISO timestamp`);
};

const requireTimezone = (record: Record<string, unknown>, key: string, issues: IssueSink) => {
  if (typeof record[key] !== 'string' || !IANA_TIMEZONE_SHAPE.test(record[key] as string) || (record[key] as string).length > 80) {
    issue(issues, 'invalid_timezone', key, `${key} must have an IANA-style timezone shape`);
  }
};

const validateRevision = (record: Record<string, unknown>, key: 'selectionRevision' | 'availabilityRevision', required: boolean, issues: IssueSink) => {
  if (!hasOwn(record, key)) {
    if (required) issue(issues, 'missing_correlation_field', key, `${key} is required`);
    return;
  }
  if (!required) issue(issues, 'forbidden_correlation_field', key, `${key} is not allowed for this event type`);
  if (!isPositiveInteger(record[key])) issue(issues, 'invalid_revision', key, `${key} must be a positive integer`);
};

const validateSubmitAttempt = (record: Record<string, unknown>, required: boolean, issues: IssueSink) => {
  if (!hasOwn(record, 'submitAttemptId')) {
    if (required) issue(issues, 'missing_correlation_field', 'submitAttemptId', 'submitAttemptId is required');
    return;
  }
  if (!required) issue(issues, 'forbidden_correlation_field', 'submitAttemptId', 'submitAttemptId is not allowed for this event type');
  if (!isIdentifier(record.submitAttemptId)) issue(issues, 'invalid_identifier', 'submitAttemptId', 'submitAttemptId must be a non-empty bounded opaque identifier');
};

const rejectCorrelationFieldsInsidePayload = (payload: Record<string, unknown>, issues: IssueSink) => {
  for (const key of Object.keys(payload)) {
    if (CORRELATION_FIELDS.has(key)) issue(issues, 'correlation_field_in_payload', `payload.${key}`, `${key} belongs at the event-envelope top level`);
  }
};

const rejectEventFieldsAtTopLevel = (record: Record<string, unknown>, issues: IssueSink) => {
  for (const key of Object.keys(record)) {
    if (EVENT_SPECIFIC_FIELDS.has(key)) issue(issues, 'event_field_at_top_level', key, `${key} belongs inside payload`);
  }
};

const validateEmptyPayload = (payload: Record<string, unknown>, issues: IssueSink) => validateAllowedKeys(payload, new Set(), issues, 'payload');

const validatePayload = (eventType: string, payload: unknown, issues: IssueSink) => {
  if (!isRecord(payload)) {
    issue(issues, 'invalid_payload', 'payload', 'payload must be an object');
    return;
  }
  rejectCorrelationFieldsInsidePayload(payload, issues);
  switch (eventType) {
    case 'date_selected':
      validateAllowedKeys(payload, unique(['requestedDateKey'] as const), issues, 'payload');
      if (!isDateKey(payload.requestedDateKey)) issue(issues, 'invalid_date_key', 'payload.requestedDateKey', 'requestedDateKey must be YYYY-MM-DD');
      return;
    case 'headcount_selected':
      validateAllowedKeys(payload, unique(['headcount'] as const), issues, 'payload');
      validateHeadcount(payload.headcount, issues, 'payload.headcount');
      return;
    case 'time_selected':
      validateAllowedKeys(payload, unique(['startTimeBucket', 'source'] as const), issues, 'payload');
      if (!isTimeBucket(payload.startTimeBucket)) issue(issues, 'invalid_time_bucket', 'payload.startTimeBucket', 'startTimeBucket must be HH:mm');
      if (!enumContains(TIME_SELECTION_SOURCES, payload.source)) issue(issues, 'invalid_enum', 'payload.source', 'source is invalid');
      return;
    case 'availability_result_presented':
      validateAllowedKeys(payload, unique(['availabilityDecisionId', 'requestedDateKey', 'startTimeBucket', 'headcount', 'availabilityResult'] as const), issues, 'payload');
      if (!isIdentifier(payload.availabilityDecisionId)) issue(issues, 'invalid_identifier', 'payload.availabilityDecisionId', 'availabilityDecisionId must be a bounded opaque server decision identifier');
      if (!isDateKey(payload.requestedDateKey)) issue(issues, 'invalid_date_key', 'payload.requestedDateKey', 'requestedDateKey must be YYYY-MM-DD');
      if (hasOwn(payload, 'startTimeBucket') && !isTimeBucket(payload.startTimeBucket)) issue(issues, 'invalid_time_bucket', 'payload.startTimeBucket', 'startTimeBucket must be HH:mm');
      validateHeadcount(payload.headcount, issues, 'payload.headcount');
      if (!enumContains(AVAILABILITY_RESULTS, payload.availabilityResult)) issue(issues, 'invalid_enum', 'payload.availabilityResult', 'availabilityResult is invalid');
      return;
    case 'contact_form_started':
    case 'reservation_submit_attempted':
      validateEmptyPayload(payload, issues);
      return;
    case 'reservation_validation_blocked':
      validateAllowedKeys(payload, unique(['reasonCode'] as const), issues, 'payload');
      if (!enumContains(VALIDATION_BLOCK_REASONS, payload.reasonCode)) issue(issues, 'invalid_enum', 'payload.reasonCode', 'reasonCode is invalid');
      return;
    case 'reservation_submit_transport_failed':
      validateAllowedKeys(payload, unique(['transportFailureKind'] as const), issues, 'payload');
      if (!enumContains(TRANSPORT_FAILURE_KINDS, payload.transportFailureKind)) issue(issues, 'invalid_enum', 'payload.transportFailureKind', 'transportFailureKind is invalid');
      return;
    case 'reservation_request_rejected':
      validateAllowedKeys(payload, unique(['rejectionKind', 'reservationMode'] as const), issues, 'payload');
      if (!enumContains(RESERVATION_REQUEST_REJECTION_KINDS, payload.rejectionKind)) issue(issues, 'invalid_enum', 'payload.rejectionKind', 'rejectionKind is invalid');
      if (hasOwn(payload, 'reservationMode') && !enumContains(BOOKING_FLOW_RESERVATION_MODES, payload.reservationMode)) issue(issues, 'invalid_enum', 'payload.reservationMode', 'reservationMode is invalid');
      return;
    case 'reservation_request_created':
      validateAllowedKeys(payload, unique(['reservationMode', 'initialStatus'] as const), issues, 'payload');
      if (!enumContains(BOOKING_FLOW_RESERVATION_MODES, payload.reservationMode)) issue(issues, 'invalid_enum', 'payload.reservationMode', 'reservationMode is invalid');
      if (!enumContains(['pending', 'confirmed'], payload.initialStatus)) issue(issues, 'invalid_enum', 'payload.initialStatus', 'initialStatus is invalid');
      return;
    default:
      return;
  }
};

const validateHeadcount = (value: unknown, issues: IssueSink, path: string) => {
  if (!Number.isInteger(value) || Number(value) < 1 || Number(value) > 99) issue(issues, 'invalid_headcount', path, 'headcount must be an integer from 1 to 99');
};

const validateCommonEnvelope = (record: Record<string, unknown>, issues: IssueSink) => {
  if (record.schemaVersion !== 2) issue(issues, 'invalid_schema_version', 'schemaVersion', 'schemaVersion must be 2');
  requireStringId(record, 'eventId', issues);
  requireStringId(record, 'unitId', issues);
  requireStringId(record, 'visitorId', issues);
  requireStringId(record, 'sessionId', issues);
  requireStringId(record, 'bookingFlowId', issues);
  const size = serializedBytes(record, issues);
  if (size !== null && size > MAX_EVENT_BYTES) issue(issues, 'payload_too_large', '', 'event exceeds maximum serialized size');
};

const validateClientEvent = (record: Record<string, unknown>, shape: 'clientInput' | 'persistedClient', issues: IssueSink) => {
  const allowed = new Set<string>([...CLIENT_COMMON_KEYS, 'selectionRevision', 'availabilityRevision', 'submitAttemptId']);
  if (shape === 'persistedClient') PERSISTED_CLIENT_EXTRA_KEYS.forEach(key => allowed.add(key));
  validateAllowedKeys(record, allowed, issues, '');
  rejectEventFieldsAtTopLevel(record, issues);
  if (shape === 'clientInput') {
    for (const key of CLIENT_SERVER_OWNED_FIELDS) {
      if (hasOwn(record, key)) issue(issues, 'server_field_in_client_input', key, `${key} is server-owned and forbidden in client input`);
    }
  }
  if (record.authority !== 'client') issue(issues, 'invalid_authority', 'authority', 'authority must be client');
  if (typeof record.eventType !== 'string' || !CLIENT_EVENT_TYPES.has(record.eventType)) {
    issue(issues, 'invalid_event_type', 'eventType', 'eventType is not a client booking-flow event');
  }
  validateCommonEnvelope(record, issues);
  requireTimestamp(record, 'occurredAtClient', issues);
  requireTimezone(record, 'clientTimezone', issues);
  requireIdempotencyKey(record, 'idempotencyKey', issues);
  if (shape === 'persistedClient') {
    requireTimestamp(record, 'receivedAtServer', issues);
    validateNormalization(record.normalization, issues);
  }

  const eventType = record.eventType as BookingFlowClientEventType;
  validateRevision(record, 'selectionRevision', selectionEventTypes.has(eventType), issues);
  validateRevision(record, 'availabilityRevision', availabilityEventTypes.has(eventType), issues);
  validateSubmitAttempt(record, clientSubmitEventTypes.has(eventType), issues);
  validatePayload(typeof record.eventType === 'string' ? record.eventType : '', record.payload, issues);
};

const validateNormalization = (normalization: unknown, issues: IssueSink) => {
  if (normalization === undefined) return;
  if (!isRecord(normalization)) {
    issue(issues, 'invalid_normalization', 'normalization', 'normalization must be an object');
    return;
  }
  validateAllowedKeys(normalization, unique(['clientTimeTrusted', 'clientTimezoneNormalized'] as const), issues, 'normalization');
  for (const key of ['clientTimeTrusted', 'clientTimezoneNormalized'] as const) {
    if (hasOwn(normalization, key) && typeof normalization[key] !== 'boolean') {
      issue(issues, 'invalid_normalization', `normalization.${key}`, `${key} must be boolean`);
    }
  }
};

const validateBackendEventRecord = (record: Record<string, unknown>, issues: IssueSink) => {
  const allowed = new Set<string>([...BACKEND_COMMON_KEYS, 'reservationId']);
  validateAllowedKeys(record, allowed, issues, '');
  rejectEventFieldsAtTopLevel(record, issues);
  if (record.authority !== 'backend') issue(issues, 'invalid_authority', 'authority', 'authority must be backend');
  if (typeof record.eventType !== 'string' || !BACKEND_EVENT_TYPES.has(record.eventType)) {
    issue(issues, 'invalid_event_type', 'eventType', 'eventType is not a backend booking-flow event');
  }
  validateCommonEnvelope(record, issues);
  requireSubmitCorrelation(record, issues);
  requireTimestamp(record, 'occurredAtServer', issues);
  requireIdempotencyKey(record, 'backendIdempotencyKey', issues);
  if (hasOwn(record, 'occurredAtClient')) issue(issues, 'client_field_in_backend_event', 'occurredAtClient', 'client timestamp is forbidden on backend events');
  if (hasOwn(record, 'clientTimezone')) issue(issues, 'client_field_in_backend_event', 'clientTimezone', 'client timezone is forbidden on backend events');
  const eventType = record.eventType as BookingFlowBackendEventType;
  if (eventType === 'reservation_request_created') {
    if (!isIdentifier(record.reservationId)) issue(issues, 'missing_reservation_id', 'reservationId', 'reservationId is required for reservation_request_created');
  } else if (hasOwn(record, 'reservationId')) {
    issue(issues, 'forbidden_correlation_field', 'reservationId', 'reservationId is allowed only for reservation_request_created');
  }
  validatePayload(typeof record.eventType === 'string' ? record.eventType : '', record.payload, issues);
};

const requireSubmitCorrelation = (record: Record<string, unknown>, issues: IssueSink) => {
  if (!backendSubmitEventTypes.has(record.eventType as BookingFlowBackendEventType)) return;
  validateSubmitAttempt(record, true, issues);
};

const validateRecord = <T>(value: unknown, shape: Shape): BookingFlowAnalyticsValidationResult<T> => {
  const issues: BookingFlowAnalyticsValidationIssue[] = [];
  if (!isRecord(value)) {
    return { valid: false, issues: [{ code: 'invalid_type', path: '', message: 'event must be an object' }] };
  }
  scanInputSafety(value, issues);
  if (typeof value.eventType === 'string' && !ALL_EVENT_TYPES.has(value.eventType)) issue(issues, 'invalid_event_type', 'eventType', 'unknown booking-flow event type');
  if (shape === 'clientInput') validateClientEvent(value, 'clientInput', issues);
  else if (shape === 'persistedClient') validateClientEvent(value, 'persistedClient', issues);
  else if (shape === 'backend') validateBackendEventRecord(value, issues);
  else if (value.authority === 'client') validateClientEvent(value, 'persistedClient', issues);
  else if (value.authority === 'backend') validateBackendEventRecord(value, issues);
  else issue(issues, 'invalid_authority', 'authority', 'authority must be client or backend');

  return issues.length === 0 ? { valid: true, value: value as T } : { valid: false, issues };
};

export const validateBookingFlowClientEventInput = (value: unknown): BookingFlowAnalyticsValidationResult<BookingFlowClientEventInput> => validateRecord(value, 'clientInput');
export const validatePersistedBookingFlowClientEvent = (value: unknown): BookingFlowAnalyticsValidationResult<PersistedBookingFlowClientEvent> => validateRecord(value, 'persistedClient');
export const validateBookingFlowBackendEvent = (value: unknown): BookingFlowAnalyticsValidationResult<BookingFlowBackendEvent> => validateRecord(value, 'backend');
export const validateBookingFlowAnalyticsEvent = (value: unknown): BookingFlowAnalyticsValidationResult<BookingFlowAnalyticsEvent> => validateRecord(value, 'any');
